import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import FileUploadDeloitteFormate from "../../../components/file-upload/FileUploadDeloitteFormate";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import { HandleApiActions } from "../../../redux/actions";
import { toast } from "react-toastify";

function TicketAttachmentUpload({ open, handleClose, intr_id, fetchTickets }) {
  const dispatch = useDispatch();
  const [uploadType, setUploadType] = useState("attributes");
  const [addSubFieldType, setAddSubFieldType] = useState("");
  const [addHeadder, setAddHeadder] = useState("");
  const [addFieldValue, setAddFieldValue] = useState(null);

  const [attributesData, setAttributesData] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [selectedFileAttributes, setSelectedFileAttributes] = useState([]);

  useEffect(() => {
    if (!open) {
      setUploadType("attributes");
      setAddSubFieldType("");
      setAddHeadder("");
      setAddFieldValue(null);
      setAttributesData([]);
      setSelectedComponent("");
      setSelectedFileAttributes([]);
    }
  }, [open]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const res = await Promise.resolve(
          dispatch(
            HandleApiActions({
              api_name: `/ACM/intersections/intr-component-attributes?intr_id=${intr_id}`,
              https_method: "GET",
              show_toast: false,
            })
          )
        );
        if (!res?.error && res?.data) {
          const normalized = (Array.isArray(res.data) ? res.data : []).map(
            (c) => {
              let attrs = c?.file_attributes;
              if (typeof attrs === "string") {
                try {
                  attrs = JSON.parse(attrs);
                } catch (e) { // NOSONAR: Fallback to empty array on invalid JSON string
                  attrs = [];
                }
              }
              return {
                ...c,
                file_attributes: Array.isArray(attrs) ? attrs : [],
              };
            }
          );
          setAttributesData(normalized);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (open && uploadType === "attributes") {
      fetchAttributes();
    }
  }, [open, uploadType, dispatch, intr_id]);

  const executeUpload = async (payload) => {
    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/ACM/intersections/uploadAttachmentFiles",
            https_method: "POST",
            params: payload,
            show_toast: true,
          })
        )
      );
      if (!res?.error) {
        if (fetchTickets) {
          await fetchTickets();
        }
        handleClose();
      }
    } catch (error) {
      console.error("Upload failed", error);
    }
  };

  const handleLocalFileUpload = async () => {
    if (!addFieldValue || !addSubFieldType) {
      toast.error("Please fill all required fields and upload a file.");
      return;
    }

    const payload = {
      intr_id: intr_id,
      attachments: [
        {
          attachment_name: addFieldValue.filename,
          attachment_type: "UPLOADED",
          file_format: addSubFieldType,
          heading: addHeadder,
        },
      ],
    };

    await executeUpload(payload);
  };

  const handleAttributesUpload = async () => {
    if (!selectedFileAttributes || selectedFileAttributes.length === 0) {
      toast.error("Please select at least one file attribute.");
      return;
    }

    const comp = attributesData.find((c) => c.component_id === selectedComponent);
    const attachments = selectedFileAttributes.map((attrObj) => {
      const actualValue = typeof attrObj === "object" ? attrObj.value : attrObj;
      const matchedAttr = comp?.file_attributes?.find(
        (a) => a.attribute_value === actualValue
      );

      return {
        attachment_name: actualValue,
        attachment_type: "ATTRIBUTE",
        file_format: matchedAttr?.attribute_sub_type?.toLowerCase() || "pdf",
        heading: matchedAttr?.attribute_heading || "Document",
      };
    });

    await executeUpload({ intr_id, attachments });
  };

  const handleSubmit = async () => {
    if (uploadType === "localFile") {
      await handleLocalFileUpload();
    } else if (uploadType === "attributes") {
      await handleAttributesUpload();
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title="Upload File"
      actionButtonStyle="continuous"
      successButtonName="Submit"
      rejectButtonName="Cancel"
      onClickSuccess={handleSubmit}
      onClickReject={handleClose}
      shouldBeResponsive="sm"
      maxWidth={true}
      paperSx={{ width: "100%", maxWidth: "600px" }}
    >
      <CustomRadioButtonV2
        value={uploadType}
        onChange={(e) => setUploadType(e.target.value)}
        displayDirection="row"
        data={[
          { value: "attributes", label: "Attributes" },
          { value: "localFile", label: "Local File" },
        ]}
      />

      {uploadType === "localFile" && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomSelectV2
            size={"small"}
            label={"Document Type"}
            optionsList={["XLS", "CSV", "XML", "PDF"].map((e) => ({
              value: e.toLowerCase(),
              label: e,
            }))}
            required={true}
            selectedValue={addSubFieldType}
            handleOnChange={(e) => {
              setAddSubFieldType(String(e.target.value).trim().toLowerCase());
              setAddFieldValue("");
            }}
          />

          <CustomFormTextFieldV2
            size={"small"}
            label={"Heading"}
            required={true}
            type={"text"}
            fldType={"textO"}
            value={addHeadder}
            setValue={(value) => {
              if (value.length <= 40) setAddHeadder(value);
            }}
            maxLength={40}
          />

          <FileUploadDeloitteFormate
            value={addFieldValue}
            onChange={(uploadedFile) => setAddFieldValue(uploadedFile)}
            allowedExtensions={
              addSubFieldType ? [addSubFieldType.trim().toLowerCase()] : []
            }
            disabled={addSubFieldType === ""}
          />
        </Box>
      )}

      {uploadType === "attributes" && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomSelectV2
            size={"small"}
            label={"Component Name"}
            optionsList={attributesData
              .filter((c) => {
                let attrs = c?.file_attributes;
                if (typeof attrs === "string") {
                  try {
                    attrs = JSON.parse(attrs);
                  } catch (e) { // NOSONAR: Fallback to empty array on invalid JSON string
                    attrs = [];
                  }
                }
                return Array.isArray(attrs) && attrs.length > 0;
              })
              .map((c) => ({
                label: c.component_name || `Component ${c.component_id}`,
                value: c.component_id,
              }))}
            required={true}
            selectedValue={selectedComponent}
            handleOnChange={(e) => {
              setSelectedComponent(e.target.value);
              setSelectedFileAttributes([]); // Reset file attributes on component change
            }}
          />

          {selectedComponent && (
            <CustomMultiSelectV2
              selectedValues={selectedFileAttributes}
              setSelectedValues={setSelectedFileAttributes}
              optionsList={
                attributesData
                  .find((c) => c.component_id === selectedComponent)
                  ?.file_attributes.map((a) => ({
                    label: a.attribute_heading || a.attribute_value,
                    value: a.attribute_value,
                  })) || []
              }
              label="File Attributes"
              required={true}
            />
          )}
        </Box>
      )}
    </CustomDialogBoxV2>
  );
}

TicketAttachmentUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  intr_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchTickets: PropTypes.func,
};

export default TicketAttachmentUpload;
